import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useLoggedInUserRoles } from '~/components/Auth';
import { useCompanyData } from '~/data/company';
import { Roles } from '~/data/user';
import { AbsenceCalendarLimitedReport } from '~/scenes/PortalModules/Analytics/People/AbsenceCalendarLimitedReport';
import { AbsenceCalendarReport } from '~/scenes/PortalModules/Analytics/People/AbsenceCalendarReport';
import { AbsencesReport } from '~/scenes/PortalModules/Analytics/People/AbsencesReport';
import { HeadcountReport } from '~/scenes/PortalModules/Analytics/People/HeadcountReport';
import { PersonnelContactsReport } from '~/scenes/PortalModules/Analytics/People/PersonnelContactsReport';
import { PersonnelListCustomReport } from '~/scenes/PortalModules/Analytics/People/PersonnelListCustomReport';
import { PersonnelListReport } from '~/scenes/PortalModules/Analytics/People/PersonnelListReport';
import { RolesListReport } from '~/scenes/PortalModules/Analytics/People/RolesListReport';
import { SubstituteManagerReport } from '~/scenes/PortalModules/Analytics/People/SubstituteManagerReport';
import { VacationBalancesReport } from '~/scenes/PortalModules/Analytics/People/VacationBalancesReport';
import { VacationCalendarReport } from '~/scenes/PortalModules/Analytics/People/VacationCalendarReport';
import { CompensationEqualityReport } from '~/scenes/PortalModules/Analytics/Rewarding/CompensationEqualityReport';
import {
  NavigationRoutingStructure,
  useStructuredModuleRouting,
} from '~/scenes/PortalViews/components/StructuredModuleRouter';
import { hasOneOfRoles } from '~/services/Permissions';

export const useAnalyticsViews = () => {
  const { t } = useTranslation();

  const { companyData } = useCompanyData();

  const companyConfig = useMemo(() => {
    return {
      showAbsenceCalendarToAll: companyData.config.fComAbsCalendarShowAll,
    };
  }, [companyData]);

  const myRoles = useLoggedInUserRoles();

  const hasRoleAccess = useCallback(
    (roles: Roles[]) => {
      return hasOneOfRoles(myRoles, roles);
    },
    [myRoles],
  );

  /**
   * Old roles mapping
   * iHR = HR_STAFF, HR_ADMIN, HR_ADMIN_LIMITED, HR_DEV (Included but might be a mistake)
   * iManager = MANAGER, ALT_MANAGER
   * ROLE-HR_STAFF-MANAGER = iHR & iManager
   */

  const initialNavigationStructure: NavigationRoutingStructure[] =
    useMemo(() => {
      const { showAbsenceCalendarToAll } = companyConfig;

      const fullHRs = [Roles.HR_ADMIN, Roles.HR_ADMIN_LIMITED, Roles.HR_STAFF];
      const fullManagers = [Roles.MANAGER, Roles.ALT_MANAGER];
      const fullManagersAndHRs = [...fullHRs, ...fullManagers];

      const peopleHRs = [...fullHRs, Roles.HR_PEOP, Roles.HR_PEOP_DEV];
      const fullManagersAndPeopleHRs = [...fullManagers, ...peopleHRs];

      const rewardingHRs = [...fullHRs];

      const performanceHRs = [...fullHRs];

      const developmentHRs = [...fullHRs, Roles.HR_DEV, Roles.HR_PEOP_DEV];

      return [
        {
          path: 'company-dashboard',
          label: t('laCompDB'),
        },
        {
          path: 'team-dashboard',
          label: t('laTeamDB'),
          isAvailable: hasRoleAccess([
            ...fullManagersAndPeopleHRs,
            Roles.DEPUTY_MANAGER,
          ]),
          subPaths: [
            {
              path: 'team-report',
              label: t('laTeamRep'),
              isAvailable: hasRoleAccess(fullManagersAndPeopleHRs),
            },
            {
              path: 'absence-calendar',
              label: t('laAbsCal'),
              isAvailable: hasRoleAccess([
                ...fullManagersAndPeopleHRs,
                Roles.DEPUTY_MANAGER,
              ]),
            },
          ],
        },
        {
          path: 'hr-dashboard',
          label: t('laHRDB'),
          isAvailable: hasRoleAccess([...peopleHRs, Roles.HR_DEV]),
        },
        {
          path: 'employee-dashboard',
          label: t('laEmpDB'),
        },
        {
          path: 'work-experience-dashboard',
          label: t('laWEDB'),
        },
        {
          path: 'people',
          label: t('laPeople'),
          subPaths: [
            {
              path: 'test',
              label: 'Test',
              component: <>Lorem ipsum</>,
              isAvailable: false,
            },
            {
              path: 'headcount-report',
              label: t('laHeadCountReport'),
              component: <HeadcountReport />,
              isAvailable: hasRoleAccess([
                ...fullManagersAndPeopleHRs,
                Roles.HR_DEV,
                Roles.HR_SALARY,
              ]),
            },
            {
              path: 'personnel-list',
              label: t('laPersonelList'),
              component: <PersonnelListReport />,
              isAvailable: hasRoleAccess([
                ...fullManagersAndPeopleHRs,
                Roles.HR_DEV,
                Roles.IT_ADMIN,
                Roles.HR_SALARY,
              ]),
            },
            {
              path: 'personnel-list-custom',
              label: t('laCustomPersonelList'),
              component: <PersonnelListCustomReport />,
              isAvailable: hasRoleAccess([
                ...fullManagersAndHRs,
                Roles.HR_SALARY,
              ]),
            },
            {
              path: 'personnel-contacts',
              label: t('laContactReport'),
              component: <PersonnelContactsReport />,
              isAvailable: hasRoleAccess([
                ...peopleHRs,
                Roles.HR_DEV,
                Roles.HR_SALARY,
              ]),
            },
            {
              path: 'roles-list',
              label: t('laRoleList'),
              component: <RolesListReport />,
              isAvailable: hasRoleAccess([
                ...peopleHRs,
                Roles.MANAGER,
                Roles.IT_ADMIN,
                Roles.HR_SALARY,
              ]),
            },
            {
              path: 'substitute-manager',
              label: t('laSubstituteManagerReport'),
              component: <SubstituteManagerReport />,
              isAvailable: hasRoleAccess([...fullManagersAndHRs, Roles.HR_DEV]),
            },
            {
              path: 'absences',
              label: t('laAbsencesReport'),
              component: <AbsencesReport />,
              isAvailable: hasRoleAccess([
                ...fullManagersAndPeopleHRs,
                Roles.DEPUTY_MANAGER,
                Roles.HR_DEV,
                Roles.HR_SALARY,
              ]),
            },
            {
              path: 'absence-calendar',
              label: t('laAbsCal'),
              component: <AbsenceCalendarReport />,
              isAvailable: hasRoleAccess([
                ...fullManagersAndPeopleHRs,
                Roles.DEPUTY_MANAGER,
                Roles.HR_DEV,
                Roles.HR_SALARY,
              ]),
            },
            {
              path: 'vacation-and-absence-calendar',
              label: t('laVacAbsCalendar'),
              component: <AbsenceCalendarLimitedReport />,
              isAvailable: showAbsenceCalendarToAll,
            },
            {
              path: 'vacations',
              label: t('laVacationCalendar'),
              component: <VacationCalendarReport />,
              isAvailable: !showAbsenceCalendarToAll,
            },
            {
              path: 'vacation-balances',
              label: t('laVacationBalancesReport'),
              component: <VacationBalancesReport />,
              isAvailable: hasRoleAccess([
                ...fullManagersAndPeopleHRs,
                Roles.HR_DEV,
                Roles.HR_SALARY,
              ]),
            },
          ],
        },
        {
          path: 'rewarding',
          label: t('laRewarding'),
          subPaths: [
            {
              path: 'compensation-equality',
              label: t('laCompensationEqualityReport'),
              component: <CompensationEqualityReport />,
              isAvailable: hasRoleAccess([...rewardingHRs, Roles.HR_SALARY]),
            },
          ],
        },
        {
          path: 'performance',
          label: t('laPerformance'),
          isAvailable: hasRoleAccess([...performanceHRs]),
        },
        {
          path: 'development',
          label: t('laDevelopment'),
          isAvailable: hasRoleAccess([...developmentHRs, Roles.IT_ADMIN]),
        },
        // {
        //   path: 'organization',
        //   label: t('laOrganization'),
        // },
        // {
        //   path: 'succession',
        //   label: t('laSuccPlan'),
        // },
      ];
    }, [t, hasRoleAccess, companyConfig]);

  return useStructuredModuleRouting(initialNavigationStructure);
};
